import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";
import { SocketContext } from "../../context/Socket/SocketContext";
import n8n from "../../assets/n8n.png";
import dialogflow from "../../assets/dialogflow.png";
import webhooks from "../../assets/webhook.png";
import typebot from "../../assets/typebot.jpg";

import { makeStyles } from "@material-ui/core/styles";

import {
  Avatar,
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";

import {
  DeleteOutline,
  Edit
} from "@material-ui/icons";

import SearchIcon from "@material-ui/icons/Search";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import {
  useViewMode,
  ViewModeToggle,
  RecordsCardsGrid,
  RecordCard,
  RecordCardMeta,
} from "../../components/ViewModeToggle";
import IntegrationModal from "../../components/QueueIntegrationModal";
import ConfirmationModal from "../../components/ConfirmationModal";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePlans from "../../hooks/usePlans";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";

const TYPE_META = {
  dialogflow: { label: "Dialogflow", src: dialogflow },
  n8n: { label: "N8N", src: n8n },
  webhook: { label: "Webhook", src: webhooks },
  typebot: { label: "Typebot", src: typebot },
};

const reducer = (state, action) => {
  if (action.type === "LOAD_INTEGRATIONS") {
    const queueIntegration = action.payload;
    const newIntegrations = [];

    queueIntegration.forEach((integration) => {
      const integrationIndex = state.findIndex((u) => u.id === integration.id);
      if (integrationIndex !== -1) {
        state[integrationIndex] = integration;
      } else {
        newIntegrations.push(integration);
      }
    });

    return [...state, ...newIntegrations];
  }

  if (action.type === "UPDATE_INTEGRATIONS") {
    const queueIntegration = action.payload;
    const integrationIndex = state.findIndex((u) => u.id === queueIntegration.id);

    if (integrationIndex !== -1) {
      state[integrationIndex] = queueIntegration;
      return [...state];
    } else {
      return [queueIntegration, ...state];
    }
  }

  if (action.type === "DELETE_INTEGRATION") {
    const integrationId = action.payload;

    const integrationIndex = state.findIndex((u) => u.id === integrationId);
    if (integrationIndex !== -1) {
      state.splice(integrationIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }

  return state;
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    margin: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  avatar: {
    width: "140px",
    height: "40px",
    borderRadius: 4
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(6),
    color: theme.palette.text.secondary,
  },
}));

const QueueIntegration = () => {
  const classes = useStyles();

  const [loading, setLoading] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [deletingIntegration, setDeletingIntegration] = useState(null);
  const [integrationModalOpen, setIntegrationModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [viewMode, setViewMode] = useViewMode("queueIntegration");
  const [queueIntegration, dispatch] = useReducer(reducer, []);
  const { user } = useContext(AuthContext);
  const { getPlanCompany } = usePlans();
  const companyId = user.companyId;
  const history = useHistory();

  const socketManager = useContext(SocketContext);

  useEffect(() => {
    async function fetchData() {
      if (!companyId) return;
      try {
        const planConfigs = await getPlanCompany(undefined, companyId);
        if (!planConfigs?.plan?.useIntegrations) {
          toast.error("Tu plan no incluye Integraciones. Te redirigimos al inicio.");
          setTimeout(() => {
            history.push(`/`);
          }, 1000);
        }
      } catch (err) {
        toastError(err);
      }
    }
    fetchData();
  }, [companyId, getPlanCompany, history]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchIntegrations = async () => {
        try {
          const { data } = await api.get("/queueIntegration/", {
            params: { searchParam, pageNumber },
          });
          dispatch({ type: "LOAD_INTEGRATIONS", payload: data.queueIntegrations || [] });
          setHasMore(Boolean(data.hasMore));
        } catch (err) {
          toastError(err);
        } finally {
          setLoading(false);
        }
      };
      fetchIntegrations();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber]);

  useEffect(() => {
    if (!companyId) return undefined;
    const socket = socketManager.getSocket(companyId);

    socket.on(`company-${companyId}-queueIntegration`, (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_INTEGRATIONS", payload: data.queueIntegration });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_INTEGRATION", payload: +data.integrationId });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [socketManager, companyId]);

  const handleOpenIntegrationModal = () => {
    setSelectedIntegration(null);
    setIntegrationModalOpen(true);
  };

  const handleCloseIntegrationModal = () => {
    setSelectedIntegration(null);
    setIntegrationModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditIntegration = (queueIntegration) => {
    setSelectedIntegration(queueIntegration);
    setIntegrationModalOpen(true);
  };

  const handleDeleteIntegration = async (integrationId) => {
    if (!integrationId || deleting) return;
    setDeleting(true);
    try {
      await api.delete(`/queueIntegration/${integrationId}`);
      dispatch({ type: "DELETE_INTEGRATION", payload: Number(integrationId) });
      toast.success(i18n.t("queueIntegration.toasts.deleted"));
    } catch (err) {
      toastError(err);
    } finally {
      setDeleting(false);
      setDeletingIntegration(null);
      setConfirmModalOpen(false);
    }
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          deletingIntegration
            ? `${i18n.t("queueIntegration.confirmationModal.deleteTitle")} ${deletingIntegration.name}?`
            : i18n.t("queueIntegration.confirmationModal.deleteTitle")
        }
        open={confirmModalOpen}
        onClose={() => {
          setConfirmModalOpen(false);
          setDeletingIntegration(null);
        }}
        onConfirm={() => handleDeleteIntegration(deletingIntegration?.id)}
        confirmDisabled={deleting || !deletingIntegration}
      >
        {i18n.t("queueIntegration.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <IntegrationModal
        open={integrationModalOpen}
        onClose={handleCloseIntegrationModal}
        aria-labelledby="form-dialog-title"
        integrationId={selectedIntegration && selectedIntegration.id}
      />
      <MainHeader>
        <Title>{i18n.t("queueIntegration.title")} ({queueIntegration.length})</Title>
        <MainHeaderButtonsWrapper>
          <TextField
            placeholder={i18n.t("queueIntegration.searchPlaceholder")}
            type="search"
            value={searchParam}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="secondary" />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenIntegrationModal}
          >
            {i18n.t("queueIntegration.buttons.add")}
          </Button>
          <ViewModeToggle value={viewMode} onChange={setViewMode} />
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper
        className={classes.mainPaper}
        variant="outlined"
        onScroll={handleScroll}
      >
        {viewMode === "cards" ? (
          <RecordsCardsGrid>
            {queueIntegration.map((integration) => {
              const typeMeta = TYPE_META[integration.type] || { label: integration.type || "—" };
              return (
                <RecordCard
                  key={`int-card-${integration.id}`}
                  title={integration.name}
                  actions={
                    <>
                      <IconButton size="small" onClick={() => handleEditIntegration(integration)}>
                        <Edit color="secondary" />
                      </IconButton>
                      <IconButton size="small" onClick={() => { setDeletingIntegration(integration); setConfirmModalOpen(true); }}>
                        <DeleteOutline color="secondary" />
                      </IconButton>
                    </>
                  }
                >
                  <RecordCardMeta>{typeMeta.label} · ID {integration.id}</RecordCardMeta>
                </RecordCard>
              );
            })}
          </RecordsCardsGrid>
        ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center">{i18n.t("queueIntegration.table.type")}</TableCell>
              <TableCell align="center">{i18n.t("queueIntegration.table.id")}</TableCell>
              <TableCell align="center">{i18n.t("queueIntegration.table.name")}</TableCell>
              <TableCell align="center">{i18n.t("queueIntegration.table.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {queueIntegration.map((integration) => {
              const typeMeta = TYPE_META[integration.type] || { label: integration.type || "—" };
              return (
                <TableRow key={integration.id}>
                  <TableCell align="center">
                    {typeMeta.src ? (
                      <Avatar src={typeMeta.src} className={classes.avatar} alt={typeMeta.label} />
                    ) : (
                      <Chip size="small" label={typeMeta.label} />
                    )}
                  </TableCell>
                  <TableCell align="center">{integration.id}</TableCell>
                  <TableCell align="center">
                    <Box display="flex" alignItems="center" justifyContent="center" style={{ gap: 8 }}>
                      <span>{integration.name}</span>
                      <Chip size="small" label={typeMeta.label} />
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Editar integración">
                      <IconButton
                        size="small"
                        onClick={() => handleEditIntegration(integration)}
                      >
                        <Edit color="secondary" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Eliminar integración">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setDeletingIntegration(integration);
                          setConfirmModalOpen(true);
                        }}
                      >
                        <DeleteOutline color="secondary" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
            {loading && <TableRowSkeleton columns={4} />}
            {!loading && queueIntegration.length === 0 && (
              <TableRow>
                <TableCell colSpan={4}>
                  <Box className={classes.emptyState}>
                    <Typography variant="h6" gutterBottom>
                      No hay integraciones
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Crea un proyecto Dialogflow, N8N, Webhook o Typebot para vincularlo a departamentos y conexiones.
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleOpenIntegrationModal}
                    >
                      {i18n.t("queueIntegration.buttons.add")}
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        )}
      </Paper>
    </MainContainer>
  );
};

export default QueueIntegration;
